/* Advisory warnings — client side of the locked contract: every
 * validate/save response carries `warnings: string[]`, one sentence
 * each, "[code] location: …". This module parses them DEFENSIVELY
 * (unknown codes / shapes degrade to strip-only display, never throw),
 * matches them to visual node keys, and fetches them (debounced) via
 * POST /validate/{type} for the read-side visuals. */
import {
    useEffect,
    useRef,
    useState
} from 'react';
import {
    appEngineService
} from '../api/appEngine.service';
import {
    useAppStudio
} from '../AppStudioContext';
import type {
    DefType
} from '../api/types';

export interface ParsedAdvisory {
    /** Stable code off the "[code]" prefix; null when absent. */
    code: string | null;
    /** Every 'single-quoted' token, in order of appearance. */
    ids: string[];
    /** The sentence without its code prefix. */
    text: string;
    /** The full original warning line. */
    raw: string;
}

export function parseAdvisory(warning: any): ParsedAdvisory {
    const raw = String(warning ?? '');
    const m = /^\s*\[([\w-]+)\]\s*([\s\S]*)$/.exec(raw);
    const text = m ? m[2] : raw;
    const ids: string[] = [];
    const re = /'([^']+)'/g;
    let q = re.exec(text);
    while (q != null) {
        ids.push(q[1]);
        q = re.exec(text);
    }
    return {
        code: m ? m[1] : null,
        ids,
        text,
        raw
    };
}

// Codes whose FIRST quoted id is NOT a node location (doc-level advice).
const DOC_LEVEL_CODES = new Set([
    'shared-flag',
    'missing-journey',
    'granted-nowhere',
    'dup-name'
]);

/** The node-locating id of a warning: the first quoted token, unless
 * the code is known to be doc-level. Defensive — unknown codes keep
 * the first-token guess so future advisories still badge sensibly. */
export function advisoryNodeId(p: ParsedAdvisory): string | null {
    if (p.code && DOC_LEVEL_CODES.has(p.code)) return null;
    return p.ids.length > 0 ? p.ids[0] : null;
}

/** keyResolver maps a warning's located id to the visual's node key
 * (journey: identity; workflow: 's:<dotted-path>' last segment). */
export function matchWarningsToKeys(warnings: string[], keys: string[],
    resolve: (id: string, key: string) => boolean): Map<string, string[]> {
    const map = new Map<string, string[]>();
    for (const w of warnings || []) {
        const p = parseAdvisory(w);
        const id = advisoryNodeId(p);
        if (!id) continue;
        for (const key of keys) {
            if (resolve(id, key)) {
                map.set(key, [...(map.get(key) || []), p.raw]);
                break;
            }
        }
    }
    return map;
}

/** Journey graphs: node key IS the surface id. */
export function journeyKeyMatch(id: string, key: string): boolean {
    return id === key;
}

/** Workflow graphs: step nodes are 's:<path.dotted.ids>' (frames
 * 'frame:s:…') — the warning's step id is the LAST path segment. */
export function workflowKeyMatch(id: string, key: string): boolean {
    const k = key.startsWith('frame:') ? key.slice('frame:'.length) : key;
    if (!k.startsWith('s:')) return false;
    const segs = k.slice(2).split('.');
    return segs[segs.length - 1] === id;
}

const DEBOUNCE_MS = 600;

/** Debounced advisory fetch for a visual: POST /validate/{type} with
 * the CURRENT doc whenever the (parsed) text changes. null = nothing
 * fetched yet; fetch failures keep the last good list. */
export function useDocWarnings(type: DefType, docText: string): {
    warnings: string[] | null;
    checking: boolean;
} {
    const {
        tenancy
    } = useAppStudio();
    const [warnings, setWarnings] = useState<string[] | null>(null);
    const [checking, setChecking] = useState(false);
    const seqRef = useRef(0);

    useEffect(() => {
        let doc: any = null;
        try {
            doc = JSON.parse(docText);
        } catch {
            return undefined; // not parseable — keep the last list
        }
        const seq = seqRef.current + 1;
        seqRef.current = seq;
        setChecking(true);
        const timer = window.setTimeout(() => {
            appEngineService.validate(tenancy, type, doc)
                .then((res) => {
                    if (seqRef.current === seq) setWarnings(res.warnings);
                })
                .catch(() => undefined)
                .finally(() => {
                    if (seqRef.current === seq) setChecking(false);
                });
        }, DEBOUNCE_MS);
        return () => {
            window.clearTimeout(timer);
            if (seqRef.current === seq) setChecking(false);
        };
    }, [tenancy, type, docText]);

    return {
        warnings,
        checking
    };
}
